'use client';

import React from 'react';
import { type HitsPerPageProps, useHitsPerPage } from 'react-instantsearch';

/**
 * Configuration par défaut des options de hits par page
 *
 * Principe Clean Code:
 * - Constante exportée pour réutilisabilité
 * - Valeur par défaut: 90 (même configuration que l'ancien moteur)
 */
export const DEFAULT_HITS_PER_PAGE_OPTIONS: HitsPerPageProps['items'] = [
  {
    value: 30,
    label: '30',
  },
  {
    value: 60,
    label: '60',
  },
  {
    value: 90,
    label: '90',
    default: true,
  },
];

/**
 * Props pour HitsPerPageSelector
 */
interface HitsPerPageSelectorProps {
  /**
   * Options de hits par page
   * @default DEFAULT_HITS_PER_PAGE_OPTIONS
   */
  items?: HitsPerPageProps['items'];
  /**
   * Classe CSS additionnelle
   */
  className?: string;
  hideUi?: boolean;
}

/**
 * Composant HitsPerPageSelector - Sélecteur du nombre de résultats par page
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage et gestion du sélecteur
 *
 * Principe SoC:
 * - Utilise le hook InstantSearch useHitsPerPage
 * - Pas de logique métier complexe
 *
 * Principe DRY:
 * - Configuration externalisée dans une constante
 * - Réutilisable dans différents contextes
 *
 * Bonnes pratiques InstantSearch:
 * - Utilise useHitsPerPage pour la gestion de l'état
 * - Gère le refine automatiquement
 *
 * Bonnes pratiques React:
 * - Composant contrôlé avec value
 * - Props typées avec TypeScript
 *
 * Accessibilité:
 * - Select natif pour compatibilité
 * - Label visuel pour clarté
 */
export function HitsPerPageSelector({
  items = DEFAULT_HITS_PER_PAGE_OPTIONS,
  className = '',
  hideUi = false,
}: HitsPerPageSelectorProps) {
  const { items: refinedItems, refine } = useHitsPerPage({ items });

  // Trouver l'item actif
  const currentItem = refinedItems.find((item) => item.isRefined);
  const currentValue = currentItem?.value ?? items[0]?.value ?? 90;
  if (hideUi) return null;
  return (
    <select
      value={String(currentValue)}
      onChange={(e) => refine(Number(e.target.value))}
      className={`select select-bordered select-sm w-auto min-w-16 text-xs ${className}`}
      aria-label="Nombre de résultats par page"
    >
      {refinedItems.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
}
